/**
 * The Reference data screen's rules, as pure functions (f2.2).
 *
 * Kept out of the components so they can be tested without rendering, and so
 * the screen reads as layout. Nothing here knows about React, fetching or the
 * lookup types themselves -- a type the build has never heard of goes through
 * exactly the same code.
 */

import type { ErrorItem, LookupType, ResourceBuMapping } from '@/api/client';

/**
 * The server's field-level problems, keyed by the field they name.
 *
 * The envelope's `errors` carry a `key` precisely so a message can be shown
 * against the control that caused it rather than as a banner the user has to
 * map back to a field themselves. An entry with no key is dropped here and
 * surfaces as the dialog's general message instead.
 */
export function problemsByKey(errors: readonly ErrorItem[]): Record<string, string> {
  const byKey: Record<string, string> = {};
  for (const error of errors) {
    if (error.key && !(error.key in byKey)) byKey[error.key] = error.message;
  }
  return byKey;
}

/**
 * Whether a value's code is one application logic resolves.
 *
 * Reserved is the SERVER's word, carried on the type: the screen never holds
 * a list of protected codes, because that list would drift from the registry
 * the moment a phase reserves another one.
 */
export function isReserved(lookupType: LookupType, code: string): boolean {
  return lookupType.reserved_codes.includes(code);
}

/**
 * Aliases as the Admin types them -- one per line, or comma separated.
 *
 * One control edits the whole set, because the set is what the server
 * replaces: there is no alias id to address a single spelling by.
 */
export function parseAliases(text: string): string[] {
  const seen = new Set<string>();
  const kept: string[] = [];
  for (const part of text.split(/[\n,]/)) {
    const alias = part.trim().replace(/\s+/g, ' ');
    const key = alias.toLocaleLowerCase();
    if (alias && !seen.has(key)) {
      seen.add(key);
      kept.push(alias);
    }
  }
  return kept;
}

/** The stored set, as text for that one control. */
export function formatAliases(aliases: readonly string[]): string {
  return aliases.join('\n');
}

/**
 * The client-side filter over the Resource BU mapping table.
 *
 * Substring, case-insensitive, over the raw value and the canonical PSU only
 * -- this narrows a list already in the browser. It is NOT resolution: the
 * server matches a feed value exactly, after normalisation, and never by
 * substring.
 */
export function matchesFilter(mapping: ResourceBuMapping, query: string): boolean {
  const wanted = query.trim().toLocaleLowerCase();
  if (!wanted) return true;
  return (
    mapping.raw_value.toLocaleLowerCase().includes(wanted) ||
    mapping.canonical_psu.toLocaleLowerCase().includes(wanted)
  );
}

/** Whether an edit form holds anything to send. */
export function hasChanges(change: Record<string, unknown>): boolean {
  return Object.keys(change).length > 0;
}
